import React from 'react';
import { MapPin, Star, TrendingUp, Users, Award, Zap } from 'lucide-react';
import { aboutData } from '../aboutData';

const AboutUs = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-black via-gray-900 to-black py-20 px-4">
        <div className="container mx-auto text-center">
          <h1 className="text-5xl md:text-7xl font-black text-white mb-6">
            About <span className="text-yellow-400">bHyyve</span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Connecting Canada with trusted businesses, one booking at a time.
          </p>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-4xl">
          <h2 className="text-4xl font-bold text-black mb-8 text-center">Our Story</h2>
          <div className="prose prose-lg max-w-none">
            <p className="text-gray-700 leading-relaxed mb-6 text-lg">
              bHyyve was born from a simple frustration: finding reliable service providers shouldn't be this hard. 
              Founded in 2020, we set out to create a platform that connects customers and businesses with verified, 
              professional service providers across Canada.
            </p>
            <p className="text-gray-700 leading-relaxed mb-6 text-lg">
              What started in Toronto has grown into Ontario's fastest-growing service marketplace. Today, we're proud 
              to serve 12 cities with over 150 verified professionals ready to help with everything from home repairs 
              to business services.
            </p>
            <p className="text-gray-700 leading-relaxed text-lg">
              Our mission is simple: make booking services as easy as ordering food, while being able to do that too. We're building the future of 
              product and service delivery, one booking at a time.
            </p>
          </div>
        </div>
      </section>

      {/* Canadian Map Section */}
      <section className="py-16 px-4 bg-gradient-to-b from-black via-gray-900 to-black">
        <div className="container mx-auto">
          <div className="flex items-center justify-center gap-3 mb-12">
            <MapPin className="text-yellow-400" size={36} />
            <h2 className="text-4xl font-bold text-white text-center">Where We Serve</h2>
          </div>

          <div className="max-w-6xl mx-auto">
            <div className="bg-gray-900 border-2 border-yellow-400 rounded-2xl p-8 mb-8">
              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-white mb-2">Serving 12 Cities Across Ontario</h3>
                <p className="text-gray-400">Our growing network of service providers</p>
              </div>
              
              {/* City Grid */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {aboutData.cities.map((city, index) => (
                  <div
                    key={index}
                    className="bg-black/50 border border-gray-700 hover:border-yellow-400 rounded-xl p-4 text-center transition-all duration-300 hover:-translate-y-1"
                  >
                    <MapPin className="mx-auto text-yellow-400 mb-2" size={24} />
                    <h4 className="text-white font-bold text-lg mb-1">{city.name}</h4>
                    <p className="text-gray-400 text-sm">{city.bookings.toLocaleString()} bookings</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="text-center">
              <p className="text-gray-400 text-lg mb-4">
                Expanding to more cities soon!
              </p>
              <a href="#contact" className="inline-block bg-yellow-400 hover:bg-yellow-500 text-black font-bold py-3 px-8 rounded-xl transition-all duration-200">
                Request Your City
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Customer Reviews Section */}
      <section className="py-16 px-4">
        <div className="container mx-auto">
          <div className="flex items-center justify-center gap-3 mb-12">
            <Star className="text-yellow-400 fill-yellow-400" size={36} />
            <h2 className="text-4xl font-bold text-black text-center">What Our Customers Say</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {aboutData.reviews.map((review) => (
              <div
                key={review.id}
                className="bg-white border-2 border-gray-200 hover:border-yellow-400 rounded-2xl p-6 transition-all duration-300 hover:shadow-lg"
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(review.rating)].map((_, i) => (
                    <Star key={i} className="text-yellow-400 fill-yellow-400" size={20} />
                  ))}
                </div>
                <p className="text-gray-700 leading-relaxed mb-6 italic">
                  "{review.text}"
                </p>
                <div className="border-t-2 border-gray-100 pt-4">
                  <p className="font-bold text-black">{review.name}</p>
                  <p className="text-gray-500 text-sm">{review.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="container mx-auto max-w-5xl">
          <h2 className="text-4xl font-bold text-black mb-12 text-center">Our Values</h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-yellow-400 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="text-black" size={32} />
              </div>
              <h3 className="text-xl font-bold text-black mb-3">Customer First</h3>
              <p className="text-gray-600">
                Every decision we make is guided by what's best for our customers and service providers.
              </p>
            </div>

            <div className="text-center">
              <div className="bg-yellow-400 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Award className="text-black" size={32} />
              </div>
              <h3 className="text-xl font-bold text-black mb-3">Quality Matters</h3>
              <p className="text-gray-600">
                We verify every service provider to ensure you get reliable, professional service every time.
              </p>
            </div>

            <div className="text-center">
              <div className="bg-yellow-400 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Zap className="text-black" size={32} />
              </div>
              <h3 className="text-xl font-bold text-black mb-3">Innovation</h3>
              <p className="text-gray-600">
                We're constantly improving our platform to make booking services faster and easier.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-black">
        <div className="container mx-auto text-center max-w-3xl">
          <h2 className="text-4xl font-bold text-white mb-6">
            Join the bHyyve Community
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Whether you need a service or want to provide one, we're here to help
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="/" className="bg-yellow-400 hover:bg-yellow-500 text-black font-bold py-4 px-8 rounded-xl transition-all duration-200">
              Download App
            </a>
            <a href="#vendor-signup" className="bg-transparent border-2 border-yellow-400 text-yellow-400 hover:bg-yellow-400 hover:text-black font-bold py-4 px-8 rounded-xl transition-all duration-200">
              Become a Provider
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;
